import './AddRate.less';

import Icon, { ArrowRightOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Form,
  InputNumber,
  Modal,
  Select,
  Tag,
  Tooltip,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useRateCreate } from '../hooks/apiHooks/rate';
import { getInAddressTerminals } from '../library/util';
import { Carrier } from '../models/Carrier';
import { Rate } from '../models/Rate';
import { ShipType } from '../models/ShipType';
import { AddressType } from '../models/Terminal';
import AddressSearchInput from './common/AddressSearchInput';
import ButtonToggle from './common/ButtonToggle';
import AddBusinessSvg from './icons/AddBusinessSvg';
import FuelSurchargeSvg from './icons/FuelSurchargeSvg';
import SettingsEthernetSvg from './icons/SettingsEthernetSvg';
import OrgDestTerminalAssignModal from './modals/OrgDestTerminalAssignModal';

interface IProps {
  carriers?: Carrier[];
  onGoToRate?: (rateId: number) => void;
  rates?: Rate[];
  selectedCarrier?: Carrier;
  shipTypes: ShipType[];
  showAllCarriers?: boolean;
}

const AddRate: React.FC<IProps> = ({
  carriers,
  onGoToRate,
  rates,
  selectedCarrier,
  shipTypes,
  showAllCarriers,
}) => {
  const createMutation = useRateCreate();
  const [form] = Form.useForm();
  const [currentCarrier, setCurrentCarrier] = useState<Carrier | undefined>(
    selectedCarrier
  );
  const [isTerminalAssignOpened, setIsTerminalAssignOpened] =
    useState<AddressType | null>(null);
  const [originTerminals, setOriginTerminals] = useState<Array<number>>();
  const [destinationTerminals, setDestinationTerminals] =
    useState<Array<number>>();
  const [currentOrigin, setCurrentOrigin] = useState<string>('');
  const [currentDestination, setCurrentDestination] = useState<string>('');

  const findDuplicateRate = useCallback(
    (input: Rate): Rate | undefined => {
      if (!rates) return undefined;
      return rates.find(
        (rate) =>
          rate.origin === input.origin &&
          rate.shipTypeId === input.shipTypeId &&
          rate.destination === input.destination &&
          rate.destinationShipTypeId === input.destinationShipTypeId &&
          rate.modeId === input.modeId
      );
    },
    [rates]
  );

  const onSubmit = useCallback(
    (input: Rate) => {
      if (currentCarrier) {
        input.carrierId = currentCarrier.id;
      }
      input.active = true;
      input.originTerminals = originTerminals;
      input.destinationTerminals = destinationTerminals;

      const existingRate = findDuplicateRate(input);
      if (existingRate) {
        Modal.confirm({
          cancelText: 'Cancel',
          content:
            'A rate with the entered origin, destination, types, and mode already exists.',
          okText: 'Go To Rate',
          onOk: () => {
            if (onGoToRate) {
              onGoToRate(existingRate.id);
            }
          },
          title: 'Duplicate Rate',
        });
        return;
      }

      createMutation.mutate(input);
    },
    [
      createMutation,
      currentCarrier,
      destinationTerminals,
      findDuplicateRate,
      onGoToRate,
      originTerminals,
    ]
  );

  const onFieldsChange = useCallback(
    (changedValues: any, allValues: any) => {
      if (changedValues.carrierId) {
        const newCarrier = carriers?.find(
          (c) => c.id === changedValues.carrierId
        );
        setCurrentCarrier(newCarrier);

        // Clear form except for the carrier selection when carrier changes
        const currentCarrierId = changedValues.carrierId;
        form.resetFields();
        form.setFieldsValue({ carrierId: currentCarrierId });

        // Clear related state
        setOriginTerminals(undefined);
        setDestinationTerminals(undefined);
        setCurrentOrigin('');
        setCurrentDestination('');
      }
      if (changedValues.origin) {
        setCurrentOrigin(changedValues.origin);
      }
      if (changedValues.destination) {
        setCurrentDestination(changedValues.destination);
      }
    },
    [carriers, form]
  );

  const onTerminalsAssign = useCallback(
    (type: AddressType) => {
      const origin = form.getFieldValue('origin');
      const destination = form.getFieldValue('destination');

      if (
        !currentCarrier ||
        (type === 'origin' && !origin) ||
        (type === 'destination' && !destination)
      ) {
        return;
      }

      setIsTerminalAssignOpened(type);
    },
    [currentCarrier, form]
  );

  const getShipTypesSelect = useCallback(
    (type: AddressType) => {
      let loc = '';
      if (type === 'origin') {
        loc = currentOrigin;
      } else {
        loc = currentDestination;
      }
      let currentTerminals = getInAddressTerminals(
        currentCarrier?.terminals,
        loc
      );
      return (
        <Select showSearch filterOption={filterOption} optionLabelProp="label">
          {shipTypes.map((shipType) => (
            <React.Fragment key={shipType.id}>
              <Select.Option
                key={shipType.id}
                label={shipType.name}
                value={shipType.id}
              >
                <div className="add-rate__ship-type">
                  <span>{shipType.name}</span>
                  {shipType.name === 'Terminal' &&
                    currentTerminals.length > 0 && (
                      <Button
                        icon={<Icon component={AddBusinessSvg} />}
                        type="link"
                        onClick={() => onTerminalsAssign(type)}
                      />
                    )}
                </div>
              </Select.Option>
            </React.Fragment>
          ))}
        </Select>
      );
    },
    [
      onTerminalsAssign,
      currentCarrier,
      shipTypes,
      currentOrigin,
      currentDestination,
    ]
  );

  const handleTerminalsAssign = useCallback(
    (terminals: Array<number>) => {
      if (isTerminalAssignOpened === 'origin') {
        setOriginTerminals(terminals);
      } else {
        setDestinationTerminals(terminals);
      }
      setIsTerminalAssignOpened(null);
    },
    [isTerminalAssignOpened]
  );

  useEffect(() => {
    setCurrentCarrier(selectedCarrier);
    // Reset form when carrier changes via URL navigation or becomes undefined
    form.resetFields();
    setOriginTerminals(undefined);
    setDestinationTerminals(undefined);
    setCurrentOrigin('');
    setCurrentDestination('');
  }, [selectedCarrier, form]);

  useEffect(() => {
    if (createMutation.isSuccess) {
      form.resetFields();
      setOriginTerminals(undefined);
      setDestinationTerminals(undefined);
    }
  }, [createMutation.isSuccess, form]);

  const availableTerminals = useMemo(() => {
    if (!isTerminalAssignOpened) {
      return [];
    }
    return getInAddressTerminals(
      currentCarrier?.terminals,
      form.getFieldValue(isTerminalAssignOpened)
    );
  }, [currentCarrier?.terminals, form, isTerminalAssignOpened]);

  const filterOption = (input: any, option: any) => {
    return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  };

  const carrierFuelSurcharge = currentCarrier?.option?.fuelSurcharge ?? 0;

  return (
    <>
      <Form
        className="add-rate"
        form={form}
        onFinish={onSubmit}
        onValuesChange={onFieldsChange}
      >
        <Card>
          <div className="add-rate__layout">
            <div className="add-rate__row">
              {showAllCarriers && (
                <div className="add-rate__field">
                  <div className="add-rate__label">Carrier</div>
                  <Form.Item
                    name="carrierId"
                    rules={[{ message: 'Required', required: true }]}
                  >
                    <Select showSearch filterOption={filterOption}>
                      {carriers?.map((carrier) => (
                        <Select.Option
                          key={carrier.id}
                          label={carrier.name}
                          value={carrier.id}
                        >
                          {carrier.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              )}

              <div className="add-rate__field add-rate__field--md">
                <div className="add-rate__label">Origin</div>
                <Form.Item
                  name="origin"
                  rules={[{ message: 'Required', required: true }]}
                >
                  <AddressSearchInput />
                </Form.Item>
              </div>

              <div className="add-rate__field add-rate__field--type">
                <div className="add-rate__label">Origin Type</div>
                <Form.Item
                  name="shipTypeId"
                  rules={[{ message: 'Required', required: true }]}
                >
                  {getShipTypesSelect('origin')}
                </Form.Item>
              </div>

              <div aria-hidden="true" className="add-rate__arrow">
                <ArrowRightOutlined />
              </div>

              <div className="add-rate__field add-rate__field--md">
                <div className="add-rate__label">Destination</div>
                <Form.Item
                  name="destination"
                  rules={[{ message: 'Required', required: true }]}
                >
                  <AddressSearchInput />
                </Form.Item>
              </div>

              <div className="add-rate__field add-rate__field--type">
                <div className="add-rate__label">Destination Type</div>
                <Form.Item
                  name="destinationShipTypeId"
                  rules={[{ message: 'Required', required: true }]}
                >
                  {getShipTypesSelect('destination')}
                </Form.Item>
              </div>

              {currentCarrier && (
                <div className="add-rate__field add-rate__field--mode">
                  <div className="add-rate__label">Mode</div>
                  <Form.Item
                    name="modeId"
                    rules={[{ message: 'Required', required: true }]}
                  >
                    <Select showSearch filterOption={filterOption}>
                      {currentCarrier.modes
                        ?.filter((mode) => mode.active)
                        .map((mode) => (
                          <Select.Option
                            key={mode.id}
                            label={mode.name}
                            value={mode.id}
                          >
                            {mode.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </div>
              )}
            </div>

            {currentCarrier && (
              <div className="add-rate__row add-rate__row--details">
                <div className="add-rate__classes">
                  {currentCarrier.classes?.map((classification) => (
                    <div
                      key={classification.id}
                      className="add-rate__field add-rate__field--class"
                    >
                      <div className="add-rate__label">
                        <Tooltip title={classification.name}>
                          <span className="add-rate__truncate">
                            {classification.name}
                          </span>
                        </Tooltip>
                      </div>
                      <Form.Item
                        name={['classRate', classification.id.toString()]}
                      >
                        <InputNumber controls={false} type="number" />
                      </Form.Item>
                    </div>
                  ))}
                </div>

                <div className="add-rate__actions">
                  <div className="add-rate__fuel">
                    <Form.Item hidden name="skipFuelSurcharge" />
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue, setFieldsValue }) => {
                        const skipFuel = !!getFieldValue('skipFuelSurcharge');
                        const inheritFuel = !skipFuel;
                        return (
                          <Tooltip
                            title={
                              inheritFuel
                                ? 'Remove fuel surcharge'
                                : 'Apply fuel surcharge'
                            }
                          >
                            <Tag
                              color={inheritFuel ? 'green' : 'default'}
                              icon={<Icon component={FuelSurchargeSvg} />}
                              style={{ cursor: 'pointer' }}
                              onClick={() =>
                                setFieldsValue({
                                  skipFuelSurcharge: inheritFuel,
                                })
                              }
                            >
                              Fuel surcharge ({carrierFuelSurcharge}%) {''}
                              {inheritFuel ? 'applied' : 'not applied'}
                            </Tag>
                          </Tooltip>
                        );
                      }}
                    </Form.Item>
                  </div>

                  <div className="add-rate__action">
                    <Form.Item name="transitional">
                      <ButtonToggle
                        icon={<Icon component={SettingsEthernetSvg} />}
                        tooltip="Transitional"
                      />
                    </Form.Item>
                  </div>

                  <Form.Item>
                    <Button
                      className="add-rate__submit-button"
                      htmlType="submit"
                      loading={createMutation.isLoading}
                      type="primary"
                    >
                      Add Rate
                    </Button>
                  </Form.Item>
                </div>
              </div>
            )}
          </div>
        </Card>
      </Form>

      {isTerminalAssignOpened && availableTerminals.length > 0 && (
        <OrgDestTerminalAssignModal
          terminals={availableTerminals}
          type={isTerminalAssignOpened}
          onClose={() => setIsTerminalAssignOpened(null)}
          onSubmit={handleTerminalsAssign}
        />
      )}
    </>
  );
};

export default AddRate;
